import { createTheme, type Theme } from '@mui/material/styles';

/**
 * MUI has no built-in monospace token, but HXIQ needs one everywhere
 * identifiers are rendered (part numbers, lot numbers, barcodes). Declaring it
 * on the theme keeps it a single source of truth rather than a repeated
 * string literal in `sx`.
 */
declare module '@mui/material/styles' {
  interface TypographyVariants {
    fontFamilyMonospace: string;
  }
  interface TypographyVariantsOptions {
    fontFamilyMonospace?: string;
  }
}

/**
 * One token set drives web and both Capacitor shells.
 * Rationale for each choice is in docs/04-ui-ux.md#design-language.
 */
export const brand = {
  teal900: '#083B40',
  teal700: '#0F5C63',
  teal500: '#178089',
  teal100: '#D6ECEE',
  amber700: '#8F5A0C',
  amber500: '#C77D14',
  /** Dark-mode amber: the same hue lifted so dark text sits on it. */
  amber300: '#E0A040',
  amber100: '#FBEEDA',
  /** Light-mode warning: MUI's #ED6C02 is 3.11:1 on white, too faint for chip text. */
  warning700: '#B14E00',
} as const;

/**
 * Contrast, measured (WCAG 2.x relative luminance, scripted — not eyeballed).
 * Text needs 4.5:1; every pairing below was checked against both papers.
 *
 *   light  secondary.main amber700 #8F5A0C on paper #FFFFFF   5.78  on default #F7F9F9  5.47
 *   light  #FFFFFF on amber700 (filled secondary chip/button) 5.78
 *   light  amber700 on amber100 tint #FBEEDA (needs-review)   5.05
 *   dark   amber500 #C77D14 on paper #121E21                  5.17  on default #0B1416  5.66
 *   dark   #FFFFFF on amber500 (filled secondary)             3.30  FAIL — hence amber300 + dark text
 *   dark   secondary.main amber300 #E0A040 on paper #121E21   7.52  on default #0B1416  8.24
 *   dark   #10201F on amber300 (filled secondary)             7.43
 *   light  text.secondary #4A5C5C on paper                    7.06  on default          6.68
 *   dark   text.secondary #9FB2B2 on paper                    7.69  on default          8.42
 *   light  MUI warning.main #ED6C02 on paper (outlined chip)  3.11  FAIL; #FFFFFF on it 3.11  FAIL
 *   light  warning700 #B14E00 on paper                        5.30  on default          5.02
 *   light  #FFFFFF on warning700 (filled warning chip)        5.30
 *   dark   MUI warning.main #FFA726 on paper                  8.76  #10201F on it       8.66  (kept)
 */

export function createAppTheme(mode: 'light' | 'dark'): Theme {
  const isLight = mode === 'light';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isLight ? brand.teal700 : brand.teal500,
        dark: brand.teal900,
        light: brand.teal100,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: isLight ? brand.amber700 : brand.amber300,
        light: brand.amber100,
        dark: brand.amber700,
        // White fails on any amber light enough to read on the dark paper;
        // the filled chip carries dark text there instead.
        contrastText: isLight ? '#FFFFFF' : '#10201F',
      },
      // MUI's dark-mode warning passes as-is; the light one does not.
      ...(isLight ? { warning: { main: brand.warning700, contrastText: '#FFFFFF' } } : {}),
      background: {
        default: isLight ? '#F7F9F9' : '#0B1416',
        paper: isLight ? '#FFFFFF' : '#121E21',
      },
      text: {
        primary: isLight ? '#10201F' : '#E6EDEC',
        secondary: isLight ? '#4A5C5C' : '#9FB2B2',
      },
      divider: isLight ? 'rgba(16,32,31,0.12)' : 'rgba(230,237,236,0.14)',
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif',
      // Monospace identifiers: part numbers get compared character-by-character
      // by humans, and proportional fonts make 0/O and 1/l ambiguous.
      fontFamilyMonospace:
        '"JetBrains Mono Variable", "JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
      h1: { fontSize: 'clamp(2.25rem, 5vw, 3.75rem)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 },
      h2: { fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 700, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.5rem', fontWeight: 650, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.25rem', fontWeight: 650 },
      h5: { fontSize: '1.05rem', fontWeight: 600 },
      h6: { fontSize: '0.95rem', fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Safe areas handled once, globally — never per screen.
          // See docs/03-architecture.md#capacitor.
          'html, body, #root': { height: '100%' },
          body: {
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            WebkitFontSmoothing: 'antialiased',
            overscrollBehaviorY: 'none',
          },
          ':focus-visible': {
            outline: `2px solid ${brand.teal500}`,
            outlineOffset: 2,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // Gloves. See docs/04-ui-ux.md#responsive--capacitor-rules.
          sizeLarge: { minHeight: 48, paddingInline: 24, fontSize: '1rem' },
          root: { minHeight: 40 },
        },
      },
      MuiTextField: { defaultProps: { variant: 'outlined', fullWidth: true } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiLink: { defaultProps: { underline: 'hover' } },
    },
  });
}
